/*
 * Copyright (c) 2025 Autire Technologies, LLC. All Rights Reserved.
 *
 * This product and related documentation is protected by copyright and
 * distributed under licenses restricting its use, copying, distribution and
 * decompilation. No part of this product or related documentation may be
 * reproduced in any form by any means without prior written authorization
 * from Autire Technologies, LLC and/or its licensors.
 */

/**
 * @fileoverview Browser history configuration for React Router
 * @description Creates and exports a browser history instance for React Router navigation.
 * This history object manages the browser's URL and provides navigation methods
 * for programmatic routing throughout the application.
 *
 * @module AppHistory
 * @author PKP Technologies, LLC
 * @since 1.0.0
 * @version 1.0.0
 *
 * @example
 * import history from './appHistory';
 *
 * // Navigate to a new route
 * history.push('/dashboard');
 *
 * // Navigate back
 * history.goBack();
 *
 * // Navigate forward
 * history.goForward();
 *
 * @see {@link App.js} For router configuration
 * @see {@link routes.js} For route definitions
 */

import { createBrowserHistory } from 'history';

/**
 * Browser history instance for React Router
 * @description Provides programmatic navigation capabilities for the application.
 * Supports push, replace, go, goBack, goForward, and other navigation methods.
 *
 * @type {Object}
 * @example
 * // Navigate to a new page
 * history.push('/user/profile');
 *
 * @example
 * // Replace current page
 * history.replace('/login');
 *
 * @example
 * // Navigate with state
 * history.push('/dashboard', { from: 'login' });
 *
 * @example
 * // Listen to location changes
 * const unsubscribe = history.listen((location, action) => {
 *   console.log('Navigation:', action, location.pathname);
 * });
 *
 * @since 1.0.0
 * @author PKP Technologies, LLC
 * @see {@link createBrowserHistory} For history creation
 * @see {@link Router} For React Router integration
 */
export default createBrowserHistory();
